import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
export const Route = createFileRoute("/dashboard/new-workspace")({
	component: NewWorkspacePage,
});

function NewWorkspacePage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [name, setName] = useState("");
	const [isCreating, setIsCreating] = useState(false);

	const handleCreate = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim() || isCreating) return;
		setIsCreating(true);

		try {
			const res = await fetch("/api/workspaces", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
				body: JSON.stringify({ name: name.trim() }),
			});

			const data = await res.json();

			if (!res.ok) {
				toast.error(data.error || "Failed to create workspace");
				return;
			}

			await queryClient.invalidateQueries();
			toast.success("Workspace created");
			navigate({ to: "/dashboard" });
		} catch {
			toast.error("Failed to create workspace");
		} finally {
			setIsCreating(false);
		}
	};

	return (
		<DashboardLayout>
			{/* Header */}
				<header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
					<div>
						<p
							className="text-[11px] font-bold uppercase tracking-[0.1em] mb-2"
							style={{ fontFamily: "inherit", color: "#6B3FF2" }}
						>
							Dashboard
						</p>
						<h1
							id="new-workspace-heading"
							className="text-5xl font-semibold tracking-tighter mb-2"
							style={{ fontFamily: "'Geist', -apple-system, sans-serif" }}
						>
							New Workspace
						</h1>
						<p
							className="text-sm"
							style={{ color: "#888888" }}
						>
							Separate balance, team access, and MCP keys per workspace.
						</p>
					</div>
					<div
						className="hidden lg:block h-px flex-1 mx-8 mb-4"
						style={{ background: "linear-gradient(to right, rgba(234,40,4,0.3), transparent)" }}
					/>
				</header>

				<div
					className="relative max-w-lg p-8 overflow-hidden"
					style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
				>
					{/* Watermark */}
					<div className="absolute top-0 right-0 p-4 opacity-[0.07] pointer-events-none">
						<span className="material-symbols-outlined text-[#6B3FF2]" aria-hidden="true" style={{ fontSize: "120px" }}>
							workspaces
						</span>
					</div>

					<form onSubmit={handleCreate} aria-labelledby="new-workspace-heading" className="space-y-6 relative z-10">
						<div className="space-y-2">
							<label
								htmlFor="workspace-name"
								className="text-[10px] uppercase tracking-wide font-bold"
								style={{ fontFamily: "inherit", color: "#888888" }}
							>
								Workspace name
							</label>
							<div className="relative">
								<span
									className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2"
									aria-hidden="true"
									style={{ fontSize: "18px", color: "#888888" }}
								>
									workspaces
								</span>
								<input
									id="workspace-name"
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="e.g. Acme Marketing"
									maxLength={80}
									className="w-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
									style={{
										backgroundColor: "#0a0a0a",
										border: "1px solid rgba(255,255,255,0.1)",
										color: "#ffffff",
										fontFamily: "inherit",
									}}
									// biome-ignore lint/a11y/noAutofocus: intentional form focus
									autoFocus
								/>
							</div>
						</div>

						<button
							type="submit"
							disabled={!name.trim() || isCreating}
							className="flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-wide transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
							style={{
								fontFamily: "inherit",
								backgroundColor: "#6B3FF2",
								color: "#ffffff",
							}}
						>
							{isCreating ? (
								<span className="material-symbols-outlined animate-spin" aria-hidden="true" style={{ fontSize: "14px" }}>progress_activity</span>
							) : (
								<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", fontVariationSettings: "'FILL' 1" }}>add</span>
							)}
							{isCreating ? "Creating…" : "Create workspace"}
						</button>
					</form>
				</div>
		</DashboardLayout>
	);
}
